<!--
  Flexi › Exposure Register: the assessment in progress and the last completed
  one as cards, the previous assessments, and the "About the company" texts
  every assessment is built on. Starting, deleting and editing are for
  authors only.
-->
<section class="fvr-card fer-overview" [attr.aria-busy]="loading">
  <header class="fvr-head fer-overview__head">
    <div>
      <h2 class="fvr-title">{{ 'FLEXI_EXPOSURE.TAB' | translate }}</h2>
      <p class="fvr-subtitle">{{ 'FLEXI_EXPOSURE.OVERVIEW.SUBTITLE' | translate }}</p>
    </div>
    @if (canAuthor) {
      <button
        type="button"
        class="fas-btn fas-btn--primary"
        [disabled]="loading || starting || templatesLoading"
        (click)="openStart()"
      >
        <i class="pi" [ngClass]="starting || templatesLoading ? 'pi-spin pi-spinner' : 'pi-plus'" aria-hidden="true"></i>
        <span>{{ (starting ? 'FLEXI_EXPOSURE.FEEDBACK.STARTING' : 'FLEXI_EXPOSURE.OVERVIEW.NEW') | translate }}</span>
      </button>
    }
  </header>

  @if (departmentCount === 0) {
    <div class="fvr-warning" role="note">
      <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
      <div>
        <strong>{{ 'FLEXI_EXPOSURE.OVERVIEW.NO_DEPARTMENTS_TITLE' | translate }}</strong>
        <p class="fer-overview__alert-text">{{ 'FLEXI_EXPOSURE.OVERVIEW.NO_DEPARTMENTS' | translate }}</p>
      </div>
    </div>
  }

  @if (loading) {
    <flexi-safety-round-skeleton layout="overview" />
  }

  @if (loadFailed) {
    <p class="fas-error" role="alert">{{ 'FLEXI_EXPOSURE.LOAD_FAILED' | translate }}</p>
    <button type="button" class="fas-btn fas-btn--ghost" (click)="load()">{{ 'FLEXI_EXPOSURE.RETRY' | translate }}</button>
  }

  @for (action of busyActions | keyvalue; track action.key) {
    <p class="fvr-action-status" role="status"><i class="pi pi-spin pi-spinner" aria-hidden="true"></i>{{ action.value | translate }}</p>
  }

  @if (inProgress || lastCompleted) {
    <div class="fer-overview__cards">
      @if (inProgress; as a) {
        <article class="fer-summary fer-summary--active" aria-labelledby="fer-in-progress-title">
          <header class="fer-summary__head">
            <div>
              <p class="fvr-eyebrow fer-summary__eyebrow">
                {{ 'FLEXI_EXPOSURE.OVERVIEW.STEP_OF' | translate: { step: a.CurrentStep, total: totalSteps, name: (stepKey(a.CurrentStep) | translate) } }}
              </p>
              <h3 class="fer-summary__title" id="fer-in-progress-title">{{ a.Title }}</h3>
            </div>
            <span class="fvr-pill fvr-pill--progress">{{ 'FLEXI_EXPOSURE.STATUS.IN_PROGRESS' | translate }}</span>
          </header>
          <dl class="fer-facts">
            <div>
              <dt>{{ 'FLEXI_EXPOSURE.OVERVIEW.STARTED' | translate }}</dt>
              <dd>{{ a.CreatedOn | date: 'dd-MM-yyyy' }}</dd>
            </div>
            <div>
              <dt>{{ 'FLEXI_EXPOSURE.RESPONSIBLE' | translate }}</dt>
              <dd>
                {{ a.ResponsibleUserName || '—' }}
                @if (a.ResponsibleRoleText) {
                  <span class="fer-facts__sub">{{ a.ResponsibleRoleText }}</span>
                }
              </dd>
            </div>
            <div>
              <dt>{{ 'FLEXI_EXPOSURE.DEPARTMENTS' | translate }}</dt>
              <dd>{{ 'FLEXI_EXPOSURE.N_OF_M' | translate: { count: a.IncludedDepartments, total: a.TotalDepartments } }}</dd>
            </div>
          </dl>
          <div class="fer-summary__progress">
            <p class="fer-summary__progress-label">
              <span id="fer-in-progress-bar">{{ 'FLEXI_EXPOSURE.FACTORS_ASSESSED' | translate }}</span>
              <span>{{ 'FLEXI_EXPOSURE.N_OF_M' | translate: { count: a.AssessedFactors, total: a.TotalFactors } }}</span>
            </p>
            <div
              class="fvr-progress__track"
              role="progressbar"
              [attr.aria-valuemin]="0"
              [attr.aria-valuemax]="a.TotalFactors"
              [attr.aria-valuenow]="a.AssessedFactors"
              aria-labelledby="fer-in-progress-bar"
            >
              <div class="fvr-progress__fill" [style.width.%]="percentOf(a.AssessedFactors, a.TotalFactors)"></div>
            </div>
          </div>
          <p class="fer-summary__next">
            <span class="fvr-eyebrow">{{ 'FLEXI_EXPOSURE.OVERVIEW.NEXT_STEP' | translate }}</span>
            <span>{{ nextStepText(a) }}</span>
          </p>
          <div class="fer-summary__actions">
            @if (canAuthor) {
              <button
                type="button"
                class="fas-btn fas-btn--text fvr-danger"
                [disabled]="busyActions.has(a.Id)"
                (click)="confirmDelete(a)"
              >
                {{ 'FLEXI_EXPOSURE.OVERVIEW.DELETE' | translate }}
              </button>
            }
            <button type="button" class="fas-btn fas-btn--primary" (click)="open(a)">
              {{ (canAuthor ? 'FLEXI_EXPOSURE.OVERVIEW.CONTINUE' : 'FLEXI_EXPOSURE.OVERVIEW.OPEN') | translate }}
              <i class="pi pi-arrow-right" aria-hidden="true"></i>
            </button>
          </div>
        </article>
      }

      @if (lastCompleted; as a) {
        <article class="fer-summary" aria-labelledby="fer-last-title">
          <header class="fer-summary__head">
            <div>
              <p class="fvr-eyebrow">{{ 'FLEXI_EXPOSURE.OVERVIEW.LAST_COMPLETED' | translate }}</p>
              <h3 class="fer-summary__title" id="fer-last-title">{{ a.Title }}</h3>
            </div>
            <span class="fvr-pill fvr-pill--done">{{ 'FLEXI_EXPOSURE.STATUS.COMPLETED' | translate }}</span>
          </header>
          <dl class="fer-facts">
            <div>
              <dt>{{ 'FLEXI_EXPOSURE.OVERVIEW.COMPLETED' | translate }}</dt>
              <dd>{{ a.CompletedOn | date: 'dd-MM-yyyy' }}</dd>
            </div>
            <div>
              <dt>{{ 'FLEXI_EXPOSURE.RESPONSIBLE' | translate }}</dt>
              <dd>
                {{ a.ResponsibleUserName || '—' }}
                @if (a.ResponsibleRoleText) {
                  <span class="fer-facts__sub">{{ a.ResponsibleRoleText }}</span>
                }
              </dd>
            </div>
          </dl>
          <div class="fer-kpis" role="group" [attr.aria-label]="'FLEXI_EXPOSURE.OVERVIEW.OUTCOME_LABEL' | translate: { title: a.Title }">
            <p class="fer-kpi">
              <span class="fer-kpi__label"><span class="fer-marker fer-marker--relevant" aria-hidden="true"></span>{{ 'FLEXI_EXPOSURE.KPI.RELEVANT' | translate }}</span>
              <span class="fer-kpi__value">{{ a.RelevantCount }} <small>{{ 'FLEXI_EXPOSURE.KPI.FACTORS' | translate }}</small></span>
            </p>
            <p class="fer-kpi">
              <span class="fer-kpi__label"><span class="fer-marker fer-marker--unclear" aria-hidden="true"></span>{{ 'FLEXI_EXPOSURE.HEALTH_CHECK.UNCLEAR' | translate }}</span>
              <span class="fer-kpi__value">{{ a.UnclearCount }} <small>{{ 'FLEXI_EXPOSURE.KPI.FACTORS' | translate }}</small></span>
            </p>
            <p class="fer-kpi">
              <span class="fer-kpi__label"><span class="fer-marker fer-marker--not-relevant" aria-hidden="true"></span>{{ 'FLEXI_EXPOSURE.HEALTH_CHECK.NOT_RELEVANT' | translate }}</span>
              <span class="fer-kpi__value">{{ a.NotRelevantCount }} <small>{{ 'FLEXI_EXPOSURE.KPI.FACTORS' | translate }}</small></span>
            </p>
          </div>
          <div class="fer-summary__actions">
            <button type="button" class="fas-btn fas-btn--ghost" (click)="open(a)">
              {{ 'FLEXI_EXPOSURE.OVERVIEW.VIEW_SUMMARY' | translate }}
            </button>
            @if (a.HasReport) {
              <button type="button" class="fas-btn fas-btn--text" [disabled]="busyActions.has(a.Id)" (click)="downloadReport(a)">
                <i class="pi" [ngClass]="busyActions.has(a.Id) ? 'pi-spin pi-spinner' : 'pi-file-pdf'" aria-hidden="true"></i>
                {{ 'FLEXI_EXPOSURE.OVERVIEW.OPEN_REPORT' | translate }}
              </button>
            }
          </div>
        </article>
      }
    </div>
  }

  @if (!loading && !loadFailed) {
    <section class="fer-overview__history" aria-labelledby="fer-previous-heading">
      <div class="fer-overview__toolbar">
        <h3 class="fvr-h3" id="fer-previous-heading">{{ 'FLEXI_EXPOSURE.OVERVIEW.PREVIOUS' | translate }}</h3>
        @if (completed.length) {
          <label class="fvr-search">
            <span class="fas-sr-only">{{ 'FLEXI_EXPOSURE.OVERVIEW.SEARCH' | translate }}</span>
            <i class="pi pi-search" aria-hidden="true"></i>
            <input
              pInputText
              type="search"
              maxlength="200"
              [(ngModel)]="search"
              (ngModelChange)="applySearch()"
              [placeholder]="'FLEXI_EXPOSURE.OVERVIEW.SEARCH' | translate"
            />
          </label>
        }
      </div>

      <div class="fvr-table-card">
        <p-table
          [value]="visibleCompleted"
          dataKey="Id"
          [rows]="10"
          [(first)]="firstCompleted"
          [paginator]="visibleCompleted.length > 10"
          paginatorDropdownAppendTo="body"
          [rowHover]="true"
          styleClass="p-datatable-sm"
          [tableStyle]="{ 'min-width': '48rem' }"
        >
          <ng-template pTemplate="header">
            <tr>
              <th scope="col">{{ 'FLEXI_EXPOSURE.COLUMN.ASSESSMENT' | translate }}</th>
              <th scope="col">{{ 'FLEXI_EXPOSURE.COLUMN.COMPLETED' | translate }}</th>
              <th scope="col">{{ 'FLEXI_EXPOSURE.RESPONSIBLE' | translate }}</th>
              <th scope="col" class="fvr-num fer-overview__num">{{ 'FLEXI_EXPOSURE.DEPARTMENTS' | translate }}</th>
              <th scope="col" class="fvr-num fer-overview__num">{{ 'FLEXI_EXPOSURE.KPI.RELEVANT' | translate }}</th>
              <th scope="col" class="fer-overview__action-col"><span class="fas-sr-only">{{ 'ACTIONS' | translate }}</span></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <th scope="row" class="fer-overview__row-head">
                <div class="fer-overview__row-title">
                  <button type="button" class="fvr-link" (click)="open(row)">{{ row.Title }}</button>
                  @if (row.HasReport) {
                    <span class="fas-muted">{{ 'FLEXI_EXPOSURE.OVERVIEW.REPORT_IN_DOCUMENTS' | translate }}</span>
                  }
                </div>
              </th>
              <td class="fvr-num">{{ row.CompletedOn | date: 'dd-MM-yyyy' }}</td>
              <td>
                {{ row.ResponsibleUserName || '—' }}
                @if (row.ResponsibleRoleText) {
                  <span class="fas-muted fer-overview__sub">{{ row.ResponsibleRoleText }}</span>
                }
              </td>
              <td class="fvr-num fer-overview__num">{{ row.IncludedDepartments }}</td>
              <td class="fvr-num fer-overview__num">{{ 'FLEXI_EXPOSURE.KPI.N_FACTORS' | translate: { count: row.RelevantCount } }}</td>
              <td class="fer-overview__action-col">
                @if (row.HasReport) {
                  <button
                    type="button"
                    class="fas-btn fas-btn--ghost fer-overview__report-btn"
                    [disabled]="busyActions.has(row.Id)"
                    [attr.aria-label]="'FLEXI_EXPOSURE.OVERVIEW.REPORT_ARIA' | translate: { title: row.Title }"
                    (click)="downloadReport(row)"
                  >
                    <i class="pi" [ngClass]="busyActions.has(row.Id) ? 'pi-spin pi-spinner' : 'pi-file-pdf'" aria-hidden="true"></i>
                    {{ 'FLEXI_EXPOSURE.OVERVIEW.REPORT' | translate }}
                  </button>
                }
                @if (canAuthor) {
                  <button
                    type="button"
                    class="fas-btn fas-btn--text fvr-danger fer-overview__report-btn"
                    [disabled]="busyActions.has(row.Id)"
                    [attr.aria-label]="('FLEXI_EXPOSURE.OVERVIEW.DELETE' | translate) + ': ' + row.Title"
                    (click)="confirmDelete(row)"
                  >
                    <i class="pi pi-trash" aria-hidden="true"></i>
                    {{ 'FLEXI_EXPOSURE.OVERVIEW.DELETE' | translate }}
                  </button>
                }
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="6">
                <div class="fvr-empty">
                  <i class="pi pi-inbox" aria-hidden="true"></i>
                  <p>
                    {{ (completed.length ? 'FLEXI_EXPOSURE.OVERVIEW.NO_MATCHES' : 'FLEXI_EXPOSURE.OVERVIEW.EMPTY') | translate }}
                  </p>
                  @if (!assessments.length && canAuthor) {
                    <p class="fvr-hint">{{ 'FLEXI_EXPOSURE.OVERVIEW.EMPTY_HINT' | translate }}</p>
                  }
                </div>
              </td>
            </tr>
          </ng-template>
        </p-table>
      </div>
    </section>

    <p class="fas-note">
      <i class="pi pi-info-circle" aria-hidden="true"></i>
      <span>{{ 'FLEXI_EXPOSURE.OVERVIEW.INFO' | translate }}</span>
    </p>
  }
</section>

<!-- ═══════════ About the company ═══════════ -->
<section class="fvr-card fer-about" id="fer-about" aria-labelledby="fer-about-heading" [attr.aria-busy]="profileLoading || savingProfile">
  <header class="fvr-head">
    <div class="fer-about__heading">
      <h2 class="fvr-title fer-about__title" id="fer-about-heading">{{ 'FLEXI_EXPOSURE.ABOUT.TITLE' | translate }}</h2>
      <p class="fvr-subtitle">{{ 'FLEXI_EXPOSURE.ABOUT.INTRO' | translate }}</p>
    </div>
  </header>

  @if (profileLoading && !profile) {
    <flexi-safety-round-skeleton />
  }
  @if (profileFailed) {
    <p class="fas-error" role="alert">{{ 'FLEXI_EXPOSURE.LOAD_FAILED' | translate }}</p>
    <button type="button" class="fas-btn fas-btn--ghost" (click)="loadProfile()">{{ 'FLEXI_EXPOSURE.RETRY' | translate }}</button>
  }

  @if (profile) {
    @if (canAuthor) {
      <form class="fer-about__form" (ngSubmit)="saveProfile()" [attr.inert]="savingProfile ? '' : null">
        <div class="fvr-field">
          <label for="fer-industry">{{ 'FLEXI_EXPOSURE.ABOUT.INDUSTRY' | translate }}</label>
          <textarea
            pTextarea
            id="fer-industry"
            name="industryExposures"
            rows="5"
            maxlength="10000"
            aria-describedby="fer-industry-help"
            [(ngModel)]="profileDraft.industryExposures"
          ></textarea>
          <span class="fvr-hint" id="fer-industry-help">
            {{ 'FLEXI_EXPOSURE.ABOUT.INDUSTRY_HELP' | translate }}
            <a class="fvr-link" href="https://noa.stami.no" target="_blank" rel="noopener noreferrer">
              {{ 'FLEXI_EXPOSURE.ABOUT.OPEN_NOA' | translate }}<span class="fas-sr-only"> ({{ 'FLEXI_EXPOSURE.NEW_WINDOW' | translate }})</span>
            </a>
          </span>
        </div>
        <div class="fvr-field">
          <label for="fer-products">{{ 'FLEXI_EXPOSURE.ABOUT.PRODUCTS' | translate }}</label>
          <textarea pTextarea id="fer-products" name="productsAndServices" rows="4" maxlength="10000" [(ngModel)]="profileDraft.productsAndServices"></textarea>
        </div>
        <div class="fvr-field">
          <label for="fer-local">{{ 'FLEXI_EXPOSURE.ABOUT.LOCAL' | translate }}</label>
          <textarea pTextarea id="fer-local" name="localClarifications" rows="4" maxlength="10000" [(ngModel)]="profileDraft.localClarifications"></textarea>
        </div>
        <div class="fer-about__foot">
          <span class="fas-muted">
            @if (profile.ModifiedOn) {
              {{ 'FLEXI_EXPOSURE.ABOUT.LAST_SAVED' | translate: { date: (profile.ModifiedOn | date: 'dd-MM-yyyy HH:mm'), name: profile.ModifiedByUserName || '—' } }}
            } @else {
              {{ 'FLEXI_EXPOSURE.ABOUT.NEVER_SAVED' | translate }}
            }
          </span>
          <div class="fer-about__buttons">
            <button type="button" class="fas-btn fas-btn--ghost" [disabled]="savingProfile || !profileDirty" (click)="cancelProfile()">
              {{ 'CANCEL' | translate }}
            </button>
            <button type="submit" class="fas-btn fas-btn--primary" [disabled]="savingProfile || !profileDirty">
              @if (savingProfile) {
                <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
              }
              {{ (savingProfile ? 'FLEXI_EXPOSURE.SAVING' : 'FLEXI_EXPOSURE.SAVE') | translate }}
            </button>
          </div>
        </div>
      </form>
    } @else {
      @if (hasProfileText) {
        <div class="fer-about__read">
          <div>
            <h3 class="fer-about__label">{{ 'FLEXI_EXPOSURE.ABOUT.INDUSTRY' | translate }}</h3>
            <p class="fer-prose">{{ profile.IndustryExposures || '—' }}</p>
          </div>
          <div>
            <h3 class="fer-about__label">{{ 'FLEXI_EXPOSURE.ABOUT.PRODUCTS' | translate }}</h3>
            <p class="fer-prose">{{ profile.ProductsAndServices || '—' }}</p>
          </div>
          <div>
            <h3 class="fer-about__label">{{ 'FLEXI_EXPOSURE.ABOUT.LOCAL' | translate }}</h3>
            <p class="fer-prose">{{ profile.LocalClarifications || '—' }}</p>
          </div>
        </div>
        @if (profile.ModifiedOn) {
          <span class="fas-muted">
            {{ 'FLEXI_EXPOSURE.ABOUT.LAST_SAVED' | translate: { date: (profile.ModifiedOn | date: 'dd-MM-yyyy HH:mm'), name: profile.ModifiedByUserName || '—' } }}
          </span>
        }
      } @else {
        <p class="fas-muted">{{ 'FLEXI_EXPOSURE.ABOUT.EMPTY' | translate }}</p>
      }
    }
  }
</section>

<p-dialog
  [visible]="startOpen"
  (visibleChange)="$event ? null : closeStart()"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [closable]="!starting"
  [style]="{ width: '560px', maxWidth: '95vw' }"
  [header]="'FLEXI_EXPOSURE.START.TITLE' | translate"
>
  <div class="fer-start">
    @if (inProgress; as a) {
      <p class="fas-note">
        <i class="pi pi-info-circle" aria-hidden="true"></i>
        <span>{{ 'FLEXI_EXPOSURE.START.ALREADY_IN_PROGRESS' | translate: { title: a.Title } }}</span>
      </p>
    } @else {
      <fieldset class="fer-start__list" [disabled]="starting">
        <legend class="fer-start__legend">{{ 'FLEXI_EXPOSURE.START.PICK_TEMPLATE' | translate }}</legend>
        @for (template of templates; track template.Id) {
          <label class="fer-start__option" [class.fer-start__option--selected]="selectedTemplateId === template.Id">
            <input type="radio" name="fer-template" [value]="template.Id" [(ngModel)]="selectedTemplateId" />
            <span class="fer-start__text">
              <span class="fer-start__name">
                <strong>{{ template.Name }}</strong>
                @if (template.IsDefault) {
                  <span class="fas-badge fas-badge--primary">{{ 'FLEXI_EXPOSURE.START.BUSINESS_DEFAULT' | translate }}</span>
                }
              </span>
              @if (template.Description) {
                <span class="fas-muted">{{ template.Description }}</span>
              }
              <span class="fas-muted">{{ templateMeta(template) }}</span>
            </span>
          </label>
        }
      </fieldset>
    }
  </div>

  <ng-template pTemplate="footer">
    <div class="fas-dialog-actions fer-start__actions">
      <button type="button" class="fas-btn fas-btn--ghost" [disabled]="starting" (click)="closeStart()">{{ 'CANCEL' | translate }}</button>
      @if (inProgress) {
        <button type="button" class="fas-btn fas-btn--primary" (click)="continueInProgress()">
          {{ 'FLEXI_EXPOSURE.OVERVIEW.CONTINUE' | translate }}
        </button>
      } @else {
        <button type="button" class="fas-btn fas-btn--primary" [disabled]="starting || selectedTemplateId === null" (click)="start()">
          @if (starting) {
            <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
          }
          {{ (starting ? 'FLEXI_EXPOSURE.FEEDBACK.STARTING' : 'FLEXI_EXPOSURE.START.SUBMIT') | translate }}
        </button>
      }
    </div>
  </ng-template>
</p-dialog>

<p-confirmDialog key="ferList" [style]="{ width: '480px', maxWidth: '95vw' }"></p-confirmDialog>
